// Reading a Vite build-time env var from code that ALSO gets bundled for React Native.
//
// `import.meta.env.VITE_X` is a Vite-only construct. Three consumers, three different
// requirements, and only one shape satisfies all of them:
//
//   Vite (platform, widget, extensions) — replaces `import.meta.env.VITE_X` at build
//     time. Fine either way.
//   React Native / Metro (tuvis-mobile-app) — babel-plugin-transform-vite-meta-env
//     rewrites the exact member expression `import.meta.env.<IDENT>` to
//     `process.env.<IDENT>`. Anything else leaves a bare `import.meta` behind, which
//     babel-preset-expo's commonjs transform then REJECTS outright:
//     "`import.meta` is not supported outside of an ES module" — an Android bundling
//     failure, not a runtime warning.
//   Plain Node (node:test via tsx, no Vite) — `import.meta.env` is undefined, so
//     `import.meta.env.VITE_X` throws a TypeError at module load, making this file and
//     everything that transitively imports it (any MobX store under test) unimportable.
//
// Optional chaining (`import.meta.env?.VITE_X`) satisfies Node but BREAKS the mobile
// build, because `?.` parses as an OptionalMemberExpression that the plugin's
// MemberExpression visitor never matches. That combination shipped once and broke the
// Android bundle; hence this helper.
//
// So: keep every read as the literal `import.meta.env.<IDENT>` expression the plugin
// matches, and pass it in lazily so the TypeError under plain Node can be caught here
// instead of taking the module down.
//
//   const level = viteEnv(() => import.meta.env.VITE_LOG_LEVEL) ?? "error";
//
// Verified against all three pipelines — do not "simplify" it to a bare
// `import.meta.env ?? {}`; that reintroduces the bare `import.meta` and the mobile
// build fails again.
export function viteEnv<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}
